<template>
  <div v-if="showLeaseInfo">
    <div v-if="!forceLeaseType" class="tla-form-group tla-form-group--margin-medium">
      <label class="tla-form-group__label tla-form-group__label--required">
        {{ leaseInfoLabel }} <span>*</span>
      </label>
      <div v-if="questionLabels[0]">
        <label  class="tla-form-group__label"
          v-html="questionLabels[0]"></label>
      </div>
      <div class="tla-form-group__touch-points">
        <touchpoint v-for="(item) in leaseOptions"
          :key="item.touchpointValue"
          :collectionName="'leaseInfo'"
          :inputId="item.touchpointValue"
          :inputValue="item.touchpointValue"
          :isSelected="item.preSelect"
          :isFullWidth="true"
          :validationScope="validationScope"
          @leaseInfo="submitLeaseInfo($event)">
          <touchpointText slot="touchpoint" :content="item.touchpointName"></touchpointText>
        </touchpoint>
      </div>
      <span class="tla-form-group__message" :class="getValidationClass('leaseInfo', 'message')">
        Please select an option.
      </span>
    </div>

    <!-- Contract Term -->
    <numberInputTouchPoint
      :step="12"
      :max="60"
      :min="0"
      :slider="true"
      :name="'financeTerm'"
      :precision="0"
      :prefix="''"
      :suffix="' Months'"
      :inputId="'NotSure'"
      :inputValue="'Not Sure'"
      :collectionName="'financeTerm'"
      :validationRules="''"
      v-model="financeTerm"
      :validationScope="validationScope"
      :label="'Contract Term <span>*</span>'"
      :interval="12"
      @financeTerm="onFinanceTermChangeEvent"/>

    <!-- Annual Mileage -->
    <numberInputTouchPoint
    :step="5000"
    :max="30000"
    :min="0"
    :slider="true"
    :name="'annualMileage'"
    :precision="0"
    :prefix="''"
    :suffix="' Miles'"
    :inputId="'NotSure'"
    :inputValue="'Not Sure'"
    :collectionName="'annualMileage'"
    :validationRules="''"
    v-model="annualMileage"
    :validationScope="validationScope"
    :label="'Annual Mileage <span>*</span>'"
    :interval="5000"
    @annualMileage="onAnnualMileageChangeEvent"/>

    <!-- Upfront Deposit Months -->
    <numberInputTouchPoint
      :step="3"
      :max="12"
      :min="0"
      :slider="true"
      :name="'depositMonths'"
      :precision="0"
      :prefix="''"
      :suffix="' Months'"
      :inputId="'NotSure'"
      :inputValue="'Not Sure'"
      :collectionName="'depositMonths'"
      :validationRules="''"
      v-model="depositMonths"
      :validationScope="validationScope"
      :label="'Upfront (Months) <span>*</span>'"
      :interval="3"
      @depositMonths="onDepositMonthsChangeEvent"/>

    <!-- Dynamic Monthly Budget Slider -->
    <div v-if="showBudgetSlider" class="tla-form-group tla-form-group--margin-large">
      <label for="monthlyBudget" class="tla-form-group__label tla-form-group__label--required">
        Monthly Budget (£) <span>*</span>
      </label>
      <div v-if="questionLabels[1]">
        <label  class="tla-form-group__label"
          v-html="questionLabels[1]"></label>
      </div>
      <NumberInput
        id="monthlyBudget"
        name="monthlyBudget"
        v-model="monthlyBudget"
        :min="sliderFloor"
        :max="sliderFloor + budgetWindow"
        :step="50"
        :precision="0"
        :slider="true"
        :fieldName="'monthlyBudget'"
        data-vv-name="monthlyBudget"
        :data-vv-scope="validationScope"
        :moneyValidationClass="getValidationClass('monthlyBudget', 'input', monthlyBudgetChanged)"
        v-validate.initial="`min_value:${sliderFloor === 0 ? 50 : sliderFloor}`"
        @monthlyBudget="monthlyBudget = $event">
      </NumberInput>
      <span class="tla-form-group__message"
        :class="getValidationClass('monthlyBudget', 'message', monthlyBudgetChanged)">
        {{ errors.first(`${validationScope}.monthlyBudget`) }}
      </span>
      <vue-slider v-if="!hideSliders" v-model="sliderValue" :min="sliderFloor" :max="sliderFloor + budgetWindow" height="10" :dot-size="26"
        :tooltip="false" :interval="50" :class="{ 'vue-slider-component--no-value': monthlyBudget < sliderFloor }"></vue-slider>
    </div>

    <includeMaintenance :validationScope="validationScope" @selected="isMaintenanceRequiredSelected = $event" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component,
  Prop,
  Watch,
  Inject
} from 'vue-property-decorator';
import vueSlider from 'vue-slider-component';
import { namespace } from 'vuex-class';
import VeeValidate, { Validator } from 'vee-validate';
import { validationService } from '@/services/ValidationService';
import mobileCheckService from '@/services/MobileCheckService';
import Touchpoint from '../../models/Touchpoint';
import GenericTouchPointComponent from '../ui/GenericTouchPointComponent.vue';
import GenericTouchPointTextComponent from '../ui/GenericTouchPointTextComponent.vue';
import IncludeVehicleMaintenanceComponent from '../vehicle/IncludeVehicleMaintenanceComponent.vue';
import NumberInput from '../widgets/common/NumberInput.vue';
import NumberInputTouchPointComponent from '../ui/NumberInputTouchPointComponent.vue';
import FinanceValidationMessages from '../../validation/finance';
import ExtraData from '../../models/ExtraData';
import PurchaseType from '../../enums/PurchaseType';
import PurchaseTypeGroup from '../../enums/PurchaseTypeGroup';
import IFinance from '../../models/interfaces/IFinance';
import {
  getLowerLimit, BaseFinanceInputs, LeaseInputs, FinanceType
} from '../../utils/FinanceCalculators';

const VehicleModule = namespace('vehicleModule');

Vue.use(VeeValidate, FinanceValidationMessages);

@Component({
  components: {
    touchpoint: GenericTouchPointComponent,
    touchpointText: GenericTouchPointTextComponent,
    numberInputTouchPoint: NumberInputTouchPointComponent,
    NumberInput,
    vueSlider,
    includeMaintenance: IncludeVehicleMaintenanceComponent
  }
})
export default class LeaseInformationComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'Are you looking for a personal lease or a business lease?' })
  public leaseInfoLabel!: string;

  @Prop({ default: false })
  public showIfLease!: boolean;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public forceLeaseType!: boolean;

  @Prop({ default: false })
  public forceBusiness!: boolean;

  @Prop({ default: () => [] })
  public questionLabels!: string[];

  private depositMonthsChanged: boolean = false;

  private monthlyBudgetChanged: boolean = false;

  private currentTouchpointState: boolean = false;

  private isMaintenanceRequiredSelected: boolean = false;

  private hideSliders: boolean = false;

  private isNotSurePurchaseType: boolean = false;

  private budgetWindow: number = 1000;

  @VehicleModule.Getter public averagePrices!: any;

  get sliderValue(): number {
    // clamp into [floor, floor+window]
    const min = this.sliderFloor;
    const max = min + this.budgetWindow;
    return Math.min(Math.max(this.monthlyBudget, min), max);
  }

  set sliderValue(v: number) {
    const min = this.sliderFloor;
    const max = min + this.budgetWindow;
    // if v falls outside, it gets clamped
    if (v < min) {
      this.monthlyBudget = min;
    } else if (v > max) {
      this.monthlyBudget = max;
    } else {
      this.monthlyBudget = v;
    }
  }

  public leaseOptions: Touchpoint[] = [
    new Touchpoint(`${PurchaseType.PersonalContractHire}`, 'Personal Lease'),
    new Touchpoint(`${PurchaseType.BusinessContractHire}`, 'Business Lease'),
    new Touchpoint('NotSure', 'Not Sure')
  ];

  public submitLeaseInfo(value: string) {
    if (value !== 'NotSure') {
      this.isNotSurePurchaseType = false;
      this.$store.dispatch('financeModule/submitPurchaseTypes', [parseInt(value, 10)]);
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'leaseInfoNotSure');
    } else {
      this.isNotSurePurchaseType = true;
      this.$store.dispatch('financeModule/submitPurchaseTypes', [PurchaseType.PersonalContractHire, PurchaseType.BusinessContractHire]);
      const extraData: ExtraData = new ExtraData('leaseInfoNotSure', 'true');
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    }

    this.$validator.errors.removeById('fake-lease-info');
    this.$validator.validateAll(this.validationScope);
  }

  public get showBudgetSlider(): boolean {
    return true
      || (!this.isNotSurePurchaseType && this.purchaseTypeGroup === PurchaseTypeGroup.Lease)
      || this.currentVehicle.manufacturerId !== 34;
  }

  public get sliderFloor(): number {
    return 0;
    /*
    if (this.currentVehicle.manufacturerId !== 34) return 0;

    // We're assuming here that a value of 0 means not set. This is a reasonable assumption
    // as zeroes in these fields are not valid. It would make this code more complicated
    // to start querying the store for not sure extra data.
    const termMonths = this.financeTerm > 0 ? this.financeTerm : 48;
    const annualMileage = this.annualMileage > 0 ? this.annualMileage : 10000;

    const inputs: BaseFinanceInputs & Partial<LeaseInputs> = {
      rrp: this.averagePrices[0],
      deposit: this.depositMonths * 0,
      termMonths,
      annualMileage,
      fees: 0
    };
    const pt = this.purchaseTypes[0];
    let type: FinanceType;
    if (pt === PurchaseType.PersonalContractHire) type = 'pch';
    else if (pt === PurchaseType.BusinessContractHire) type = 'bch';
    else return 0;
    const floor = getLowerLimit(type, inputs as any);
    // clamp out infinities / NaN
    return Number.isFinite(floor) ? floor : 0;
    */
  }

  @Watch('sliderFloor')
  private clampMonthlyBudget(newFloor: number) {
    if (this.monthlyBudget < newFloor) this.monthlyBudget = newFloor;
    const max = newFloor + this.budgetWindow;
    if (this.monthlyBudget > max) this.monthlyBudget = max;
  }

  public get purchaseTypes(): PurchaseType[] {
    return this.$store.getters['financeModule/purchaseTypes'];
  }

  public get purchaseTypeGroup(): PurchaseTypeGroup {
    return this.$store.getters['financeModule/purchaseTypeGroup'];
  }

  public get currentVehicle() {
    return this.$store.getters['vehicleModule/currentVehicle'];
  }

  public get showLeaseInfo(): boolean {
    return ((this.purchaseTypeGroup === PurchaseTypeGroup.Lease && this.showIfLease) || !this.showIfLease);
  }

  @Watch('showLeaseInfo', { immediate: true })
  public onshowLeaseInfoChanged(newValue: boolean, oldValue: boolean) {
    if (newValue) {
      this.$validator.errors.add({
        field: 'fake-lease-info',
        msg: 'Please answer.',
        scope: this.validationScope,
        id: 'fake-lease-info'
      });
    } else {
      this.$validator.errors.removeById('fake-lease-info');
    }
    this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'leaseInfoNotSure');
  }

  public get finance(): IFinance {
    return this.$store.getters['financeModule/finance'];
  }

  public get financeTerm(): number {
    return this.finance.financeTerm;
  }

  public set financeTerm(value: number) {
    const clamped = Math.min(Math.max(value, 0), 60);
    this.$store.dispatch('financeModule/submitFinanceTerm', clamped);
  }

  public get depositMonths(): number {
    return this.finance.depositMonths;
  }

  public set depositMonths(value: number) {
    const clamped = Math.min(Math.max(value, 0), 12);
    this.$store.dispatch('financeModule/submitDepositMonths', clamped);
  }

  public get annualMileage(): number {
    return this.finance.annualMileage;
  }

  public set annualMileage(value: number) {
    const clamped = Math.min(Math.max(value, 0), 30000);
    this.$store.dispatch('financeModule/submitAnnualMileage', clamped);
  }

  public get monthlyBudget(): number {
    return this.finance.monthlyBudget;
  }

  public set monthlyBudget(value: number) {
    this.$store.dispatch('financeModule/submitMonthlyBudget', value);
  }

  public getValidationClass(fieldName: string, fieldType?: string, fieldChanged?: boolean) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, fieldChanged, false, this.validationScope);
  }

  @Watch('financeTerm')
  public onFinanceTermChange(newVal: number, oldVal: number) {
    if (this.financeTerm > 0 && this.currentTouchpointState) {
      this.currentTouchpointState = false;
    }
  }

  @Watch('depositMonths')
  public onDepositMonthsChange(newVal: number, oldVal: number) {
    this.depositMonthsChanged = oldVal !== newVal;
  }

  @Watch('monthlyBudget')
  public onMonthlyBudgetChange(newVal: number, oldVal: number) {
    this.monthlyBudgetChanged = oldVal !== newVal;
  }

  public onFinanceTermChangeEvent(newVal: number) {
    this.financeTerm = newVal;
  }

  public onAnnualMileageChangeEvent(newVal: number) {
    this.annualMileage = newVal;
  }

  public onDepositMonthsChangeEvent(newVal: number) {
    this.depositMonths = newVal;
  }

  public created() {
    const v: any = this.$validator;

    // swallow only the “Validating a non-existent field” errors
    const wrap = (orig: Function) => (...args: any[]) => orig(...args).catch((err: any) => {
      if (err && /Validating a non-existent field/.test(err.message)) {
        return Promise.resolve(false);
      }
      return Promise.reject(err);
    });

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
    v.validate = wrap(v.validate.bind(v));
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
    v.validateAll = wrap(v.validateAll.bind(v));
    this.$validator.localize('en', FinanceValidationMessages.english);
    this.hideSliders = mobileCheckService.hideSliders;
    if (this.forceLeaseType) {
      const forced = this.forceBusiness ? PurchaseType.BusinessContractHire : PurchaseType.PersonalContractHire;
      this.submitLeaseInfo(`${forced}`);
    }
  }
}
</script>
